import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import Button from "@components/ui/buttons/Button";
import { useRef, useState } from "react";
import TextField from "@components/ui/textfields/TextField";
import { PartnerDecennale, PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { partnersApi } from "@services/api/partners";

const companyTypeOptions = [
  { value: "gros_oeuvre", label: "Gros œuvre" },
  { value: "charpente", label: "Charpente" },
  { value: "couverture", label: "Couverture" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "enduit", label: "Enduit" },
  { value: "cloisonnement", label: "Cloisonnement" },
  { value: "carrelage", label: "Carrelage / Faïence" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "peinture", label: "Peinture" },
  { value: "parquet", label: "Parquet" },
  { value: "architecte", label: "Architecte" },
  { value: "bureau_d_etudes", label: "Bureau d'études" },
  { value: "jardinage", label: "Jardinage" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "demenagement", label: "Déménagement" },
  { value: "informatique", label: "Informatique" },
  { value: "autre_btp", label: "Autre BTP" },
  { value: "autre_service", label: "Autre service" },
  { value: "autre", label: "Autre" },
];

export const ModifyPartnerModal = ({
  onClose,
  initialPartner,
}: {
  onClose: () => void;
  initialPartner: PartnerInfos;
}) => {
  const updatePartner = useProjectStore((state) => state.updatePartner);
  const removePartners = useProjectStore((state) => state.removePartners);
  const [partner, setPartner] = useState<PartnerInfos>({ ...initialPartner });
  const [newDecennaleYear, setNewDecennaleYear] = useState<string>(
    String(new Date().getFullYear())
  );
  const [uploadingDecennale, setUploadingDecennale] = useState(false);
  const [showAddDecennale, setShowAddDecennale] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const yearOptions = [
    { value: 2024, label: "2024" },
    { value: 2025, label: "2025" },
    { value: 2026, label: "2026" },
    { value: 2027, label: "2027" },
    { value: 2028, label: "2028" },
    { value: 2029, label: "2029" },
    { value: 2030, label: "2030" },
  ];

  function handleSave() {
    if (!partner.display_name && !partner.company_name) {
      showNotification({
        type: "error",
        message: "Veuillez renseigner un nom ou une entreprise",
      });
      return;
    }
    updatePartner(partner);
    showNotification({
      type: "success",
      message: "Artisan modifié avec succès",
    });
    onClose();
  }

  async function handleUploadDecennale(file: globalThis.File) {
    const year = parseInt(newDecennaleYear, 10);
    if (isNaN(year) || year < 2000 || year > 2100) {
      showNotification({ type: "error", message: "Année invalide" });
      return;
    }
    setUploadingDecennale(true);
    try {
      const updated = await partnersApi.uploadDecennale(partner.id, year, file);
      setPartner(updated);
      updatePartner(updated);
      showNotification({ type: "success", message: `Décennale ${year} ajoutée` });
      setShowAddDecennale(false);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'upload" });
    } finally {
      setUploadingDecennale(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDeleteDecennale(year: number) {
    try {
      const updated = await partnersApi.deleteDecennale(partner.id, year);
      setPartner(updated);
      updatePartner(updated);
      showNotification({ type: "success", message: `Décennale ${year} supprimée` });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression" });
    }
  }

  return (
    <>
    <BaseModal
      title="Modifier l'intervenant"
      startIcon={<Icon1 name="intervenant" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <div className="text-h3 text-neutral-400 font-semibold">Entreprise</div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Nom entreprise"
              placeholder="Construction ABC"
              value={partner.company_name || ""}
              onChange={(value) =>
                setPartner({ ...partner, company_name: value })
              }
            />
          </div>
          <div className="flex-1">
            <DropDown
              options={companyTypeOptions}
              label="Type"
              value={partner.company_type}
              onSelect={(value) =>
                setPartner({
                  ...partner,
                  company_type: value as PartnerInfos["company_type"],
                })
              }
            />
          </div>
        </div>
        <TextField
          label="Rôle / Fonction"
          placeholder="Architecte, Maçon, Électricien..."
          value={partner.role}
          onChange={(value) => setPartner({ ...partner, role: value })}
        />
        <div className="flex flex-row gap-2">
          <TextField
            label="Tel entreprise"
            placeholder="01 23 45 67 89"
            type="phone"
            value={partner.company_phone || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_phone: value })
            }
          />
          <TextField
            label="Email entreprise"
            placeholder="contact@entreprise.fr"
            value={partner.company_email || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_email: value })
            }
          />
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Adresse"
            placeholder="12 Rue de la Paix"
            value={partner.company_address || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_address: value })
            }
          />
          <TextField
            label="Code postal"
            placeholder="75002"
            value={partner.company_postal || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_postal: value })
            }
          />
        </div>
        <TextField
          label="Ville"
          placeholder="Paris"
          value={partner.company_city || ""}
          onChange={(value) =>
            setPartner({ ...partner, company_city: value })
          }
        />

        <div className="border-t border-neutral-200 my-2" />
        <div className="text-h3 text-neutral-400 font-semibold">Contact</div>
        <TextField
          label="Nom complet"
          placeholder="Sophie Martin"
          value={partner.display_name}
          onChange={(value) =>
            setPartner({ ...partner, display_name: value })
          }
        />
        <div className="flex flex-row gap-2">
          <TextField
            label="Téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={partner.phone_number || ""}
            onChange={(value) =>
              setPartner({ ...partner, phone_number: value })
            }
          />
          <TextField
            label="Email"
            placeholder="sophie@martin.fr"
            value={partner.email || ""}
            onChange={(value) => setPartner({ ...partner, email: value })}
          />
        </div>

        <div className="border-t border-neutral-200 my-2" />
        <div className="text-h3 text-neutral-400 font-semibold">Assurances décennales</div>

        {/* Existing décennales */}
        {(partner.decennales || []).length > 0 && (
          <div className="flex flex-col gap-1">
            {(partner.decennales || [])
              .slice()
              .sort((a, b) => a.year - b.year)
              .map((d: PartnerDecennale) => (
                <div
                  key={d.year}
                  className="flex flex-row items-center gap-2 px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-200"
                >
                  <span className="text-h3 text-neutral-700 font-medium flex-1">
                    Décennale {d.year}
                  </span>
                  {d.url && (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-neutral-400 hover:text-neutral-600"
                    >
                      <Icon1 name="open" size={20} />
                    </a>
                  )}
                  <button
                    onClick={() => handleDeleteDecennale(d.year)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <Icon1 name="delete" size={20} />
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* Add new décennale */}
        <SecondaryButton variant="small" onClick={() => setShowAddDecennale(true)}>
          Ajouter une décennale
        </SecondaryButton>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-between">
        <SecondaryButton
          variant="small"
          danger={true}
          onClick={() => {
            removePartners([initialPartner.id]);
            showNotification({
              type: "success",
              message: "Artisan supprimé",
            });
            onClose();
          }}
        >
          Supprimer
        </SecondaryButton>
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button
            variant="small"
            label="Enregistrer"
            onClick={handleSave}
          />
        </div>
      </div>
    </BaseModal>
    {showAddDecennale && (
      <BaseModal
        title="Ajouter une décennale"
        onClose={() => setShowAddDecennale(false)}
      >
        <div className="bg-white flex flex-col gap-3 p-4">
          <DropDown
            options={yearOptions}
            label="Année"
            value={parseInt(newDecennaleYear, 10)}
            onSelect={(value) => setNewDecennaleYear(String(value))}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUploadDecennale(file);
            }}
          />
          <Button
            variant="small"
            label={uploadingDecennale ? "Upload..." : "Ajouter un PDF"}
            onClick={() => fileInputRef.current?.click()}
          />
        </div>
        <div className="border-t-1 p-4 flex flex-row justify-end">
          <SecondaryButton variant="small" onClick={() => setShowAddDecennale(false)}>
            Annuler
          </SecondaryButton>
        </div>
      </BaseModal>
    )}
    </>
  );
};
